/**
 * @fileoverview NotesCard component for displaying and managing notes with filtering and search functionality
 * @module components/cards/NotesCard
 * @requires react
 * @requires @mui/material
 * @requires @mui/icons-material
 * @requires ../../utils/capitalize
 * @requires ./InfoCard
 */
/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";

import {
  Box,
  Button,
  MenuItem,
  ListItemText,
  ListItemIcon,
  Popover,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import PushPinIcon from "@mui/icons-material/PushPin";
import CloseIcon from "@mui/icons-material/Close";

import CommonSearchBar from "../common/searchBar/CommonSearchBar";
import InfoCard from "./InfoCard";
import Profile from "../../assets/icons/profile.svg";
import { placeholderText } from "../../constants/colors";

import OutreachIcon from "../../assets/icons/OutreachIcon.svg";
import formatDateTimeUtil from "../../utils/formatDateTime";

/**
 * NotesCard Component
 * A component for displaying and managing notes with filtering and search capabilities.
 *
 * @param {Object} props - Component props
 * @param {Array<Object>} props.notes - Array of note objects to display
 * @param {string} [props.title="Notes"] - Title of the notes card
 * @param {Function} [props.onNoteTypeChange] - Callback when note type filter changes
 * @param {Array<string>} [props.selectedNoteType=[]] - Currently selected note types
 * @param {string} [props.searchQuery=""] - Current search query
 * @param {Function} [props.onSearch] - Callback for search functionality
 * @param {Function} [props.onTogglePin] - Callback when a note's pin status is toggled
 * @param {Function} [props.onEdit] - Callback when a note is edited
 * @param {string} [props.containerHeight=""] - Custom height for the container
 * @param {boolean} [props.inQuickview=false] - Whether the card is in quickview mode
 * @param {Array<Object>} [props.programStatusMap=[]] - Mapping of program statuses
 * @returns {JSX.Element} The NotesCard component
 */
const NotesCard = ({
  notes: propNotes,
  title = "Notes",
  onNoteTypeChange,
  selectedNoteType = [],
  searchQuery: propSearchQuery = "",
  onSearch,
  onTogglePin,
  onEdit,

  inQuickview = false,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState(propSearchQuery);
  const [selectedTypes, setSelectedTypes] = useState(
    Array.isArray(selectedNoteType) ? selectedNoteType : [],
  );
  const [error, setError] = useState(null);
  const [showFullNoteModal, setShowFullNoteModal] = useState(false);
  const [selectedNoteForModal, setSelectedNoteForModal] = useState(null);

  const filterButtonRef = useRef(null);

  const handleOpenFullNote = (note) => {
    setSelectedNoteForModal(note);
    setShowFullNoteModal(true);
  };

  const handleCloseFullNote = () => {
    setShowFullNoteModal(false);
    setSelectedNoteForModal(null);
  };

  const handleSearchChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);

    if (newValue === "") {
      onSearch("");
    }
  };
  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const noteTypes = ["All", "Archived"];

  useEffect(() => {
    if (selectedNoteType !== undefined) {
      setSelectedTypes(
        Array.isArray(selectedNoteType) ? selectedNoteType : [selectedNoteType],
      );
    }

    setSearchTerm(propSearchQuery);
  }, [selectedNoteType, propSearchQuery]);

  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleTypeToggle = (type) => {
    const newSelected = [type];

    setSelectedTypes(newSelected);
    onNoteTypeChange(newSelected);
  };

  const open = Boolean(anchorEl);
  const id = open ? "note-type-filter" : undefined;

  const [notes, setNotes] = useState(propNotes);

  React.useEffect(() => {
    if (propNotes) {
      setNotes(propNotes);
    }
  }, [propNotes]);

  const defaultSelectedTypes = noteTypes.filter(
    (type) => type.toLowerCase() !== "archived",
  );

  const effectiveSelectedTypes =
    selectedTypes.length === 0 ? [...defaultSelectedTypes] : selectedTypes;

  useEffect(() => {
    if (selectedTypes.length === 0) {
      onNoteTypeChange([...defaultSelectedTypes]);
    }
  }, []);

  const filteredNotes = (notes || [])
    .filter((note) => {
      const isArchived = note.status === "ARCHIVED";
      const noteType = note.type || "General";
      const searchLower = propSearchQuery.toLowerCase();

      if (propSearchQuery) {
        const searchFields = [note.content || ""];

        const matchesSearch = searchFields.some((field) =>
          field.toLowerCase().includes(searchLower),
        );

        if (!matchesSearch) return false;
      }

      if (effectiveSelectedTypes.includes("All") && !isArchived) {
        return true;
      }

      if (effectiveSelectedTypes.includes("Archived") && isArchived) {
        return true;
      }

      return effectiveSelectedTypes.some(
        (selectedType) => selectedType.toLowerCase() === noteType.toLowerCase(),
      );
    })
    .sort((a, b) => {
      if (a.pinNote && !b.pinNote) return -1;
      if (!a.pinNote && b.pinNote) return 1;

      const dateA = a.updateAt || a.createdAt || a.date || 0;
      const dateB = b.updateAt || b.createdAt || b.date || 0;
      return new Date(dateB) - new Date(dateA);
    });

  const systemTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <InfoCard
      title={
        <Box
          component="span"
          sx={{
            fontSize: "16px !important",
            fontWeight: "600 !important",
            color: "#403C6F !important",
            fontFamily:
              "Inter, ui-sans-serif, system-ui, sans-serif !important",
            lineHeight: "1.35 !important",
            margin: "0 !important",
          }}
        >
          {title}
        </Box>
      }
      contentPadding={0}
      showEditButton={false}
      sx={{
        "& .MuiBox-root": { mb: 0 },
        "& .MuiDivider-root": { my: 0.5 },
        ...(inQuickview && {
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }),
      }}
      quickview={inQuickview}
    >
      <Box
        pt={0.5}
        sx={{
          ...(inQuickview && {
            position: "sticky",
            top: 0,
            backgroundColor: "white",
            zIndex: 1,
            pt: 2,
            pb: 1,
          }),
        }}
      >
        <Box mb={1} sx={{ position: "relative" }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            mb={1}
            pr={2}
            gap={1}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <CommonSearchBar
                placeholder="Search notes..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e)}
                onSearch={(value) => {
                  setSearchTerm(value);
                  onSearch(value);
                }}
                sx={{
                  width: "100%",
                  "& .MuiOutlinedInput-root": {
                    height: "32px",
                    "& input": {
                      padding: "8px 12px",
                      fontSize: "14px",
                      "&::placeholder": { color: "#5A5875", opacity: 1 },
                    },
                  },
                }}
              />
            </Box>
            <Box flexShrink={0}>
              <Button
                variant="outlined"
                size="small"
                aria-haspopup="true"
                aria-controls={id}
                aria-expanded={open ? "true" : undefined}
                aria-label={`${selectedTypes.length} filters applied`}
                onClick={handleFilterClick}
                endIcon={<FilterListRoundedIcon />}
                sx={{
                  textTransform: "none",
                  color: "#403C6F",
                  borderColor: "#403C6F",
                  backgroundColor: "#ECECF1",
                  "&:hover": {
                    borderColor: "#403C6F",
                    backgroundColor: "#ECECF1",
                    color: "#403C6F",
                  },
                  "& .MuiButton-endIcon": {
                    color: "#403C6F",
                    marginLeft: "2px",
                  },
                  borderRadius: 20,
                  whiteSpace: "nowrap",
                }}
                ref={filterButtonRef}
              >
                {selectedTypes.includes("Archived") ? (
                  <span>Archived</span>
                ) : (
                  "All Notes"
                )}
              </Button>
            </Box>
            <Popover
              id={id}
              open={open}
              anchorEl={anchorEl}
              onClose={handleFilterClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              disableScrollLock
              sx={{
                ...(!inQuickview && {
                  position: "fixed !important",
                  transform: "none !important",
                  "& .MuiPopover-paper": {
                    position: "static !important",
                    transform: "none !important",
                  },
                }),
                "& .MuiPopover-paper": {
                  backgroundColor: "#6E6D92",
                  color: "#fff",
                  borderRadius: "10px",
                  boxShadow: "0px 4px 16px rgba(0,0,0,0.12)",
                  minWidth: 265,
                  width: 265,
                  maxHeight: "70vh",
                  overflowY: "auto",
                  "&::-webkit-scrollbar": { width: "6px" },
                  "&::-webkit-scrollbar-track": {
                    background: "rgba(255,255,255,0.1)",
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "rgba(255,255,255,0.3)",
                    borderRadius: "10px",
                    "&:hover": { background: "rgba(255,255,255,0.4)" },
                  },
                },
                "& .MuiMenuItem-root": {
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                },
              }}
            >
              <Box
                role="menu"
                aria-label="Note type filters"
                sx={{
                  p: 2,
                  gap: 2,
                  maxHeight: "calc(70vh - 32px)",
                  overflowY: "auto",
                  overflowX: "hidden",
                }}
              >
                {noteTypes.map((type, idx) => {
                  const isSelected = selectedTypes.includes(type);
                  return (
                    <MenuItem
                      key={type}
                      role="menuitemcheckbox"
                      aria-checked={isSelected}
                      onClick={() => handleTypeToggle(type)}
                      sx={{
                        width: 239,
                        height: 40,
                        justifyContent: "space-between",
                        opacity: 1,
                        p: 0,
                        pt: "8px",
                        pr: "16px",
                        pb: "8px",
                        pl: "16px",
                        borderRadius: "4px",
                        color: "#fff",
                        backgroundColor: isSelected ? "#403C6F" : "transparent",
                        alignItems: "center",
                        mb: idx === noteTypes.length - 1 ? 0 : "4px",
                        "&:hover": {
                          backgroundColor: isSelected
                            ? "#403C6F"
                            : "rgba(255,255,255,0.08)",
                        },
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        width="100%"
                        justifyContent="space-between"
                      >
                        <Box sx={{ flex: 1, textAlign: "left" }}>
                          <ListItemText
                            primary={type}
                            slotProps={{
                              primaryTypography: {
                                sx: {
                                  color: "#fff",
                                  textAlign: "left",
                                  margin: 0,
                                },
                              },
                            }}
                          />
                        </Box>
                        <ListItemIcon sx={{ minWidth: 32, marginLeft: "8px" }}>
                          <span
                            aria-hidden="true"
                            style={{
                              width: 16,
                              height: 16,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: 4,
                              border: "2px solid #fff",
                              backgroundColor: isSelected
                                ? "#fff"
                                : "transparent",
                            }}
                          >
                            {isSelected && (
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#403C6F"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{ display: "block" }}
                              >
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                          </span>
                        </ListItemIcon>
                      </Box>
                    </MenuItem>
                  );
                })}
              </Box>
            </Popover>
          </Box>
          <Box mb={2} />
        </Box>
        <Box
          sx={{
            flex: 1,
            overflow: "hidden",
            pr: 1,
            mt: 1,
            ...(inQuickview && {
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }),
          }}
        >
          <Stack
            spacing={1}
            sx={{
              maxHeight: inQuickview ? "calc(100vh - 450px)" : "445px",
              height: inQuickview ? "auto" : "445px",
              minHeight: inQuickview ? "300px" : "445px",
              overflowY: "auto",
              pr: 1,
              pb: 1,
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb:hover": { background: "#555" },
            }}
            tabIndex={0}
            aria-label="Notes list"
            onKeyDown={(e) => {
              const el = e.currentTarget;
              const step = 40;
              switch (e.key) {
                case "ArrowDown":
                  el.scrollBy({ top: step, behavior: "smooth" });
                  e.preventDefault();
                  break;
                case "ArrowUp":
                  el.scrollBy({ top: -step, behavior: "smooth" });
                  e.preventDefault();
                  break;
                case "PageDown":
                  el.scrollBy({ top: el.clientHeight, behavior: "smooth" });
                  e.preventDefault();
                  break;
                case "PageUp":
                  el.scrollBy({ top: -el.clientHeight, behavior: "smooth" });
                  e.preventDefault();
                  break;
                case "Home":
                  el.scrollTo({ top: 0, behavior: "smooth" });
                  e.preventDefault();
                  break;
                case "End":
                  el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
                  e.preventDefault();
                  break;
                default:
                  break;
              }
            }}
          >
            {filteredNotes.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  color: "#0E0D09",
                  fontSize: "16px",
                  fontWeight: 700,
                  flexGrow: 1,
                  minHeight: "250px",
                  height: "100%",
                }}
              >
                {searchTerm ? "No Notes found" : "No Notes available"}
              </Box>
            ) : (
              filteredNotes.map((note, i) => {
                const fullContent =
                  typeof note.content === "string"
                    ? note.content
                    : JSON.stringify(note.content);
                const isLong = fullContent.length > 256;
                const noteContent = isLong
                  ? fullContent.slice(0, 256)
                  : fullContent;

                const formatDateTime = (dateString) =>
                  formatDateTimeUtil(dateString, { timeZone: systemTimeZone });
                const creationDate = note.date || note.createdAt;
                const displayDateTime = formatDateTime(creationDate);
                const authorName = note.author || "Unknown";
                return (
                  <Box
                    key={note.id || i}
                    p={2}
                    onClick={() => {
                      if (!isLong && onEdit) onEdit(note);
                    }}
                    sx={{
                      border: "1px solid #E8E8E9",
                      borderRadius: "8px",
                      mb: 2,
                      cursor: isLong ? "default" : "pointer",
                      transition: "all 0.2s",
                      position: note.pinNote ? "sticky" : "relative",
                      top: note.pinNote ? 0 : "auto",
                      zIndex: note.pinNote ? 10 : "auto",
                      backgroundColor: note.pinNote ? "#fff" : "transparent",
                      "&:last-child": { mb: 0 },
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="flex-start"
                      gap={1}
                      mb={1.5}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          fontWeight: 500,
                          fontSize: "14px",
                          lineHeight: "20px",
                          color: "#403C6F",
                          whiteSpace: "pre-line",
                          wordBreak: "break-word",
                        }}
                      >
                        {noteContent}
                        {isLong && (
                          <Box
                            component="span"
                            sx={{
                              color: "#403C6F",
                              fontWeight: 700,
                              ml: 0.5,
                              cursor: "pointer",
                              "&:hover": { textDecoration: "underline" },
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenFullNote(note);
                            }}
                          >
                            ...Read more
                          </Box>
                        )}
                      </Box>
                      {note.pinNote && (
                        <Box
                          onClick={async (e) => {
                            e.stopPropagation();
                            const updatedNotes = notes.map((n) =>
                              n.id === note.id
                                ? { ...n, pinNote: !n.pinNote }
                                : n,
                            );
                            setNotes(updatedNotes);
                            if (onTogglePin && note.id) {
                              try {
                                await onTogglePin(note.id);
                              } catch (err) {
                                const revertedNotes = notes.map((n) =>
                                  n.id === note.id
                                    ? { ...n, pinNote: !n.pinNote }
                                    : n,
                                );
                                setNotes(revertedNotes);
                                setError(err?.message || error);
                              }
                            }
                          }}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                            flexShrink: 0,
                            ml: 1,
                            "&:hover": {
                              "& .MuiSvgIcon-root": { color: "#2A2866" },
                            },
                          }}
                        >
                          <PushPinIcon
                            fontSize="small"
                            sx={{
                              color: note.pinNote ? "#403C6F" : "#E0E0E0",
                              transition: "color 0.2s",
                              "&:hover": { color: "#2A2866" },
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: 1,
                        position: "relative",
                      }}
                    >
                      {note.type?.toLowerCase() === "outreach" && (
                        <Box sx={{ position: "absolute", right: 0, bottom: 0 }}>
                          <Tooltip
                            title={
                              <Box
                                sx={{
                                  fontWeight: 400,
                                  fontSize: "12px",
                                  lineHeight: "16px",
                                }}
                              >
                                This outreach note is added for{" "}
                                {note.programName || "the program"}
                              </Box>
                            }
                            arrow
                            placement="bottom"
                            slotProps={{
                              tooltip: {
                                sx: {
                                  bgcolor: "#403C6F",
                                  "& .MuiTooltip-arrow": {
                                    color: "#403C6F",
                                  },
                                  boxShadow: "none",
                                  "& .MuiTooltip-tooltip": {
                                    fontWeight: 400,
                                  },
                                },
                              },
                            }}
                          >
                            <Box
                              component="img"
                              src={OutreachIcon}
                              alt="Outreach"
                              sx={{
                                width: 20,
                                height: 20,
                                position: "absolute",
                                right: 0,
                                bottom: 0,
                              }}
                            />
                          </Tooltip>
                        </Box>
                      )}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <Box
                          component="img"
                          src={Profile}
                          alt="Author"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                            e.currentTarget.nextSibling.style.display =
                              "inline-flex";
                          }}
                          sx={{
                            width: "16px",
                            height: "16px",
                            filter:
                              "invert(55%) sepia(10%) saturate(1000%) hue-rotate(210deg) brightness(90%) contrast(85%)", // This approximates #7F7C9F
                          }}
                        />
                        <Box
                          sx={{
                            display: "none",
                            width: 16,
                            height: 16,
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "50%",
                            backgroundColor: "#ECECF1",
                            fontSize: 10,
                            fontWeight: 600,
                            color: "#403C6F",
                          }}
                        >
                          {(authorName || "U").charAt(0).toUpperCase()}
                        </Box>
                        <Box
                          sx={{
                            fontWeight: 600,
                            fontSize: "12px",
                            color: placeholderText,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          {authorName}
                          {displayDateTime && (
                            <>
                              <span style={{ margin: "0 1px" }}>|</span>
                              <span style={{ fontSize: "12px" }}>
                                created on: {displayDateTime}
                              </span>
                            </>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })
            )}
          </Stack>
        </Box>
      </Box>

      {/* Full Note Detail Modal */}
      <Dialog
        open={showFullNoteModal}
        onClose={handleCloseFullNote}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              bgcolor: "#F7F8FB",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 2,
          }}
        >
          <Typography
            sx={{ fontWeight: 600, fontSize: "18px", color: "#403C6F" }}
          >
            Note Details
          </Typography>
          <IconButton
            onClick={handleCloseFullNote}
            size="small"
            sx={{
              color: "#403C6F",
              "&:hover": { backgroundColor: "rgba(64, 60, 111, 0.04)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            pt: "8px !important",
            pb: 4,
            px: 3,
          }}
        >
          <Box
            sx={{
              bgcolor: "#FFFFFF",
              borderRadius: "8px",
              border: "1px solid #E8E8E9",
              p: 2,
              maxHeight: "60vh",
              overflowY: "auto",
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                background: "#88888840",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb:hover": { background: "#88888860" },
            }}
          >
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: "15px",
                lineHeight: "1.6",
                color: "#403C6F",
                whiteSpace: "pre-line",
                wordBreak: "break-word",
              }}
            >
              {selectedNoteForModal?.content}
            </Typography>
          </Box>
          {selectedNoteForModal && (
            <Box
              sx={{
                mt: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    bgcolor: "#ECECF1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#403C6F",
                  }}
                >
                  {(selectedNoteForModal.author || "U").charAt(0).toUpperCase()}
                </Box>
                <Typography variant="caption" sx={{ color: "#7F7C9F" }}>
                  {selectedNoteForModal.author}
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: "#7F7C9F" }}>
                {formatDateTimeUtil(
                  selectedNoteForModal.date || selectedNoteForModal.createdAt,
                  { timeZone: systemTimeZone },
                )}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </InfoCard>
  );
};

NotesCard.propTypes = {
  notes: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string,
  onNoteTypeChange: PropTypes.func,
  selectedNoteType: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string,
  ]),
  searchQuery: PropTypes.string,
  onSearch: PropTypes.func,
  onTogglePin: PropTypes.func,
  onEdit: PropTypes.func,
  inQuickview: PropTypes.bool,
};

export default NotesCard;
